// src/pages/Agents/GoogleCalendar/GoogleCalendarAgents.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate, Link } from 'react-router-dom';
import backendURL from "../../../config";
import { Stepper, Step, StepLabel, Button, Box, Typography, Card, CardHeader, CardContent, TextField } from '@mui/material';
import RouterAgentModal from "../RouterAgentModal";

const steps = ['Connection Details', 'Agent Backstory', 'Task Description'];

// Default prompts for the agent if none are set by the user
const DEFAULT_GCAL_BACKSTORY = `You are an expert personal assistant with full access to a user Google Calendar data.
You are equipped with tools to both read the user's schedule and to write new events to it.
You meticulously analyze the user's request to decide which tool is appropriate.
For reading events, you use the 'List Upcoming Calendar Events' tool.
For creating events, you use the 'Create Calendar Event' tool.`;

const DEFAULT_GCAL_DESCRIPTION = `The user wants to do something with their calendar. Their request is: '{user_query}'.

**CRITICAL CONTEXT: The current date and time is {current_time_iso}. The user's timezone is '{timezone_setting}'.**
You MUST use this current time and timezone as the reference for all relative date calculations (like 'tomorrow' or 'next Monday').

Your job is to analyze the user's request and take the correct action:

1. **IF THE USER IS ASKING A QUESTION** about their schedule (e.g., 'what is my next meeting?'), you MUST use the 'List Upcoming Calendar Events' tool.

2. **IF THE USER WANTS TO CREATE an event** (e.g., 'book a meeting'), you MUST use the 'Create Calendar Event' tool. 
First, use the provided current time and timezone to accurately calculate the full ISO 8601 timestamps for the start and end of the event. 
Then, call the tool with the required 'summary', 'start_time_str', 'end_time_str', and also pass the user's timezone in the 'timeZone' argument. For example, \`timeZone='{timezone_setting}'\`.`;

export default function GoogleCalendarAgents() {
    const [activeStep, setActiveStep] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isRouterModalOpen, setRouterModalOpen] = useState(false);
    const [config, setConfig] = useState({ 
        backstory: '', 
        description: '' 
    });
    const location = useLocation();
    const navigate = useNavigate();

    const fetchAgentAndConnectionStatus = useCallback(async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem("access_token");
        const headers = { Authorization: `Bearer ${token}` };

        const params = new URLSearchParams(location.search);
        if (params.has('error')) {
            setError(`Authentication failed: ${params.get('error')}. Reason: ${params.get('reason') || 'Unknown'}`);
        }

        try {
            const agentStatusResponse = await axios.get(`${backendURL}/api/agents/status`, { headers });
            const gcalConfigStatus = agentStatusResponse.data.agents.GOOGLE_CALENDAR;
            setIsConnected(gcalConfigStatus.is_configured);
            setIsActive(gcalConfigStatus.is_active);

            if (gcalConfigStatus.is_configured) {
                if (params.has('connected')) {
                    setSuccessMessage('Successfully connected to Google Calendar.');
                }
                const personalityResponse = await axios.get(`${backendURL}/api/agents/gcal/config`, { headers });
                if (personalityResponse.data.success) {
                    const fetchedConfig = personalityResponse.data.data;
                    // Use saved data, but fall back to defaults if the fields are empty
                    setConfig({
                        backstory: (fetchedConfig && fetchedConfig.backstory) || DEFAULT_GCAL_BACKSTORY,
                        description: (fetchedConfig && fetchedConfig.description) || DEFAULT_GCAL_DESCRIPTION,
                    });
                }
            } else {
                 // If not configured, pre-fill with default prompts
                setConfig({
                    backstory: DEFAULT_GCAL_BACKSTORY,
                    description: DEFAULT_GCAL_DESCRIPTION,
                });
            }
        } catch (err) {
            console.error("Error checking Google Calendar status:", err);
            if (!error) {
                setError(err.response?.data?.message || "Failed to check connection status.");
            }
            // Set defaults on error to ensure fields are populated
            setConfig({
                backstory: DEFAULT_GCAL_BACKSTORY,
                description: DEFAULT_GCAL_DESCRIPTION,
            });
        } finally {
            setIsLoading(false);
            if (params.has('error') || params.has('connected')) {
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location.search, navigate, error]);

    useEffect(() => {
        document.title = 'Google Calendar Agent | Klonit';
        fetchAgentAndConnectionStatus();
    }, [fetchAgentAndConnectionStatus]);

    const handleToggle = async (newStatus) => {
        setIsActive(newStatus);
        setError('');
        setSuccessMessage('');
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            await axios.post(`${backendURL}/api/agents/status`, { agent_name: 'GOOGLE_CALENDAR', is_active: newStatus }, { headers });
            setSuccessMessage(`Agent successfully ${newStatus ? 'activated' : 'deactivated'}.`);
        } catch (err) {
            setError('Failed to update agent status.');
            setIsActive(!newStatus);
        }
    };
    
    const handleConnect = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        const userAuthToken = localStorage.getItem("access_token");
        try {
            const response = await axios.get(`${backendURL}/api/google-calendar/auth/start`, {
                headers: { Authorization: `Bearer ${userAuthToken}` }
            });
            if (response.data && response.data.success && response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                setError(response.data.message || "Failed to initiate Google Calendar connection.");
                setIsProcessing(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not connect to Google Calendar. Please try again.");
            setIsProcessing(false);
        }
    };

    const handleDisconnect = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        const token = localStorage.getItem("access_token");
        try {
            await axios.post(`${backendURL}/api/google-calendar/disconnect`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsConnected(false);
            setIsActive(false); 
            setSuccessMessage('Successfully disconnected from Google Calendar.');
        } catch (err) {
            setError(err.response?.data?.message || "Failed to disconnect. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfigChange = (e) => {
        setConfig({ ...config, [e.target.name]: e.target.value });
    };

    const handleSaveConfig = async () => {
        setIsProcessing(true);
        setError('');
        setSuccessMessage('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/agents/gcal/config`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage(response.data.message);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);
    
    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box className="text-center">
                        {isConnected ? (
                            <>
                                <p className="text-success"><i className="fa fa-check-circle"></i> Connected to Google Calendar.</p>
                                <button className="btn btn-lg btn-danger" onClick={handleDisconnect} disabled={isProcessing}>
                                    {isProcessing ? 'Disconnecting...' : <><i className="fa fa-unlink me-2"></i>Disconnect</>}
                                </button>
                            </>
                        ) : (
                             <>
                                <p>Connect your Google Calendar account to allow the agent to manage events.</p>
                                <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isProcessing}>
                                     {isProcessing ? 'Connecting...' : <><i className="fa fa-google me-2"></i>Connect to Google Calendar</>}
                                </button>
                            </>
                        )}
                    </Box>
                );
            case 1:
                return (
                     <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>Define the agent's personality and background. This helps set the context for its responses.</Typography>
                        <TextField name="backstory" label="Agent Backstory" multiline rows={6} fullWidth value={config.backstory || ''} onChange={handleConfigChange} placeholder="e.g., You are an expert personal assistant..." />
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>Describe the specific task the agent should perform. Be detailed about its function and expected output.</Typography>
                        <TextField name="description" label="Task Description" multiline rows={8} fullWidth value={config.description || ''} onChange={handleConfigChange} placeholder="e.g., Your job is to analyze the user's request and take the correct action..." />
                    </Box>
                );
            default:
                return <Typography>Configuration Complete!</Typography>;
        }
    };

    if (isLoading) return <div className="container-fluid p-0 text-center mt-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    
    return (
        <main className="content">
            <div className="container-fluid p-0">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className="h3 d-inline align-middle"><strong>Google Calendar Agent</strong></h1>
                    <Link to="/agents" className="btn btn-secondary"><i className="fa fa-arrow-left me-2"></i>Back to Agents</Link>
                </div>

                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                {error && <div className="alert alert-danger">{error}</div>}

                <Card>
                     <CardHeader
                        title="Agent Status"
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button variant="outlined" size="small" onClick={() => setRouterModalOpen(true)}>
                                    Edit Router Agent
                                </Button>
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" role="switch" checked={isActive} onChange={(e) => handleToggle(e.target.checked)} disabled={!isConnected} id="gcal-agent-switch" />
                                    <label className="form-check-label" htmlFor="gcal-agent-switch">{isActive ? 'Active' : 'Inactive'}</label>
                                </div>
                            </Box>
                        }
                    />
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            {isActive ? "The Google Calendar agent is active and ready to handle requests." : "The agent is inactive. Activate it to allow access to your calendar."}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                            {steps.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
                        </Stepper>
                        
                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
                            {activeStep < steps.length - 1 ? (
                                <Button variant="contained" onClick={handleNext} disabled={!isConnected}>Next</Button>
                            ) : (
                                <Button variant="contained" color="primary" onClick={handleSaveConfig} disabled={isProcessing || !isConnected}>
                                    {isProcessing ? 'Saving...' : 'Save Configuration'}
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
                <RouterAgentModal open={isRouterModalOpen} onClose={() => setRouterModalOpen(false)} />
            </div>
        </main>
    );
}